import { useEffect, useState } from 'react';
import { EventQueueView, UnitStateData } from '@mark43/cad-api';
import { ButtonGroup, HStack, ModalFooter, Button, Text } from '@cad-arc';
import { useIntegratedEventQueueStore } from '../../event-queues/use-integrated-event-queue-store';
import { ModalControls } from './types';
import { closeModalWhenAedIsDone, aedEventSecondsLeft as getAedEventLeftSeconds } from './helper';
import { AED_SECONDS_LEFT_TO_ALERT, AED_WAIT_TIME_IN_SECONDS } from './constants';

interface IncomingAssignmentModalFooterAedParams {
    alternateEnRouteState?: UnitStateData;
    alternateEnRouteStateMutation: () => void;
    enRouteState?: UnitStateData;
    enRouteStateMutation: () => void;
    mutationIsLoading?: boolean;
    eventId: number;
    modalControls: ModalControls;
}

export function IncomingAssignmentModalFooterAed({
    alternateEnRouteState,
    alternateEnRouteStateMutation,
    enRouteState,
    enRouteStateMutation,
    mutationIsLoading,
    eventId,
    modalControls,
}: IncomingAssignmentModalFooterAedParams): React.ReactElement {
    const [clickedStateButton, setClickedStateButton] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(AED_WAIT_TIME_IN_SECONDS);
    const textSecondsLeftColor = secondsLeft <= AED_SECONDS_LEFT_TO_ALERT ? 'negative' : undefined;
    const { store: queueStore } = useIntegratedEventQueueStore();

    const assignedEvent = queueStore.listStore.getItemById(eventId) as EventQueueView;
    const assignedEventTimeDispatched = assignedEvent?.timeDispatched;

    useEffect(() => {
        closeModalWhenAedIsDone({ assignedEvent, modalControls });
    }, [assignedEvent, modalControls]);

    useEffect(() => {
        if (clickedStateButton && assignedEventTimeDispatched && secondsLeft > 0) {
            const aedEventLeftSecs = getAedEventLeftSeconds({
                assignedEventTimeDispatched,
                aedTimeInSeconds: AED_WAIT_TIME_IN_SECONDS,
            });

            setSecondsLeft(aedEventLeftSecs);

            if (aedEventLeftSecs <= AED_WAIT_TIME_IN_SECONDS) {
                const interval = setInterval(() => {
                    setSecondsLeft((prevSecondsLeft) => prevSecondsLeft - 1);
                }, 1000);
                return () => clearInterval(interval);
            }
        }
    }, [assignedEventTimeDispatched, clickedStateButton, secondsLeft]);
    const shouldAutoFocusEnRoute = !!enRouteState;
    const shouldAutoFocusAlternateEnRoute = !shouldAutoFocusEnRoute && !!alternateEnRouteState;

    return (
        <ModalFooter>
            <ButtonGroup>
                {clickedStateButton && (
                    <HStack w="full" justify="right" h="100%">
                        <Text fontSize="lg" color={textSecondsLeftColor}>
                            {secondsLeft}s
                        </Text>
                        <Text fontSize="lg">Waiting for second unit to accept...</Text>
                        <Button isDisabled variant="outline" isActive={false}>
                            Accepted
                        </Button>
                    </HStack>
                )}
                {!clickedStateButton && !!alternateEnRouteState && (
                    <Button
                        variant="outline"
                        isLoading={mutationIsLoading}
                        onClick={() => {
                            alternateEnRouteStateMutation();
                            setClickedStateButton(true);
                        }}
                        autoFocus={shouldAutoFocusAlternateEnRoute}
                    >
                        {alternateEnRouteState.value}
                    </Button>
                )}
                {!clickedStateButton && !!enRouteState && (
                    <Button
                        variant="solid"
                        isLoading={mutationIsLoading}
                        onClick={() => {
                            enRouteStateMutation();
                            setClickedStateButton(true);
                        }}
                        autoFocus={shouldAutoFocusEnRoute}
                    >
                        {enRouteState.value}
                    </Button>
                )}
            </ButtonGroup>
        </ModalFooter>
    );
}
